import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

const STATUSES = ["", "Given", "Declined", "Withdrawn"];
const TONE = { Given: "text-ochre", Declined: "text-zinc-500", Withdrawn: "text-maasai" };

/** Who consented to what, when, and on what wording — read from the record each consent was given in. */
export default function ConsentLog() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/consent-log", { params: { q, kind, status } });
      setD(data);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  }, [q, kind, status]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const res = await api.get("/admin/consent-log/export.csv", { params: { q }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "consent-log.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div data-testid="admin-consent-log">
      <p className="overline">Data protection</p>
      <h1 className="display-2 mt-4">Consent log</h1>
      <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">
        {d?.note || "Every consent decision we hold, with the date and the wording it was given on."}
      </p>

      <div className="mt-8 flex flex-wrap items-end gap-3">
        <div className="relative">
          <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input className="field w-72 !border-zinc-800 !pl-9 focus:!border-ochre" placeholder="Search an email address" value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="consent-search" />
        </div>
        <select className="field w-56 bg-black" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="consent-kind">
          <option value="">Every consent type</option>
          {(d?.kinds || []).map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
        </select>
        <select className="field w-44 bg-black" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="consent-status">
          {STATUSES.map((s) => <option key={s || "all"} value={s}>{s || "Any status"}</option>)}
        </select>
        <button type="button" onClick={download} className="btn-ghost inline-flex items-center gap-2" data-testid="consent-export">
          <Download size={13} /> Export CSV
        </button>
      </div>

      {d ? (
        <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-5" data-testid="consent-totals">
          {[["People", d.totals.people], ["Records", d.totals.records], ["Given", d.totals.given],
            ["Declined", d.totals.declined], ["Withdrawn", d.totals.withdrawn]].map(([label, value]) => (
            <div key={label} className="bg-ink-surface p-5">
              <p className="mono text-[0.52rem] uppercase tracking-[0.16em] text-zinc-500">{label}</p>
              <p className="mt-2 font-display text-2xl font-bold leading-none text-white">{value}</p>
            </div>
          ))}
        </div>
      ) : null}

      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-zinc-500" data-testid="consent-loading">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </p>
      ) : !d?.rows?.length ? (
        <p className="mt-10 text-sm text-zinc-500" data-testid="consent-empty">
          No consent records match. Nothing is hidden — if an address is not here, we hold no consent from it.
        </p>
      ) : (
        <div className="mt-8 overflow-x-auto border border-zinc-900">
          <table className="w-full min-w-[1000px] text-left" data-testid="consent-table">
            <thead>
              <tr className="border-b border-white/15 bg-[#0b0b0b]">
                {["Person", "Consent", "Status", "When", "Wording it was given on", "Record"].map((h) => (
                  <th key={h} className="mono px-4 py-3 text-[0.52rem] uppercase tracking-[0.16em] text-zinc-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={`${r.record_id}-${r.kind}-${i}`} className="border-b border-zinc-900" data-testid={`consent-row-${i}`}>
                  <td className="px-4 py-4 text-sm text-white">{r.email || "—"}</td>
                  <td className="px-4 py-4 text-xs text-zinc-300">
                    {r.label}
                    {r.note ? <span className="mt-1 block text-[0.65rem] text-zinc-600">{r.note}</span> : null}
                  </td>
                  <td className={`mono px-4 py-4 text-[0.55rem] uppercase tracking-[0.14em] ${TONE[r.status] || "text-zinc-400"}`}>
                    {r.status}
                  </td>
                  <td className="mono px-4 py-4 text-[0.6rem] text-zinc-400">
                    {(r.withdrawn_at || r.granted_at || "—").slice(0, 10)}
                  </td>
                  <td className="px-4 py-4 text-xs text-zinc-400">{r.basis || "—"}</td>
                  <td className="mono px-4 py-4 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                    {r.source} · {(r.record_id || "").slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
